import { Facebook, Instagram, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const SocialMedia = () => {
  const fbContainerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { rootMargin: '200px', threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || scriptsLoaded) return;

    const loadScripts = () => {
      const loadFacebookSDK = () => {
        if (document.getElementById('facebook-jssdk')) {
          if ((window as any).FB && fbContainerRef.current) {
            (window as any).FB.XFBML.parse(fbContainerRef.current);
          }
          return;
        }

        (window as any).fbAsyncInit = function() {
          (window as any).FB.init({ xfbml: true, version: 'v18.0' });
          if (fbContainerRef.current) {
            (window as any).FB.XFBML.parse(fbContainerRef.current);
          }
        };

        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/nb_NO/sdk.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      };

      const loadInstagramEmbed = () => {
        if (!document.getElementById('instagram-embed')) {
          const script = document.createElement('script');
          script.id = 'instagram-embed';
          script.src = '//www.instagram.com/embed.js';
          script.async = true;
          document.body.appendChild(script);
        } else if ((window as any).instgrm) {
          (window as any).instgrm.Embeds.process();
        }
      };

      loadFacebookSDK();
      loadInstagramEmbed();
      setScriptsLoaded(true);
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(loadScripts, { timeout: 2000 });
    } else {
      setTimeout(loadScripts, 100);
    }
  }, [isVisible, scriptsLoaded]);

  return (
    <section ref={sectionRef} aria-labelledby="social-heading" className="section-padding bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
            Følg oss
          </p>
          <h2 id="social-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
            Sosiale medier
          </h2>
          <p className="text-muted-foreground font-sans">
            Hold deg oppdatert på våre nyheter, bunader og håndverksarbeid.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Facebook Embed */}
          <div className="flex flex-col">
            <a
              href="https://www.facebook.com/www.bunadstuggu.no"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Besøk Bunadstuggu på Facebook"
              className="flex items-center justify-between mb-4 group p-4 rounded-lg border border-border hover:border-primary/50 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#1877F2]/10 flex items-center justify-center" aria-hidden="true">
                  <Facebook className="w-6 h-6 text-[#1877F2]" />
                </div>
                <div className="text-left">
                  <span className="font-sans text-base text-foreground font-medium block">
                    Bunadstuggu
                  </span>
                  <span className="font-sans text-xs text-muted-foreground">
                    @bunadstuggu
                  </span>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
            </a>
            <div 
              ref={fbContainerRef}
              className="w-full bg-card rounded-lg overflow-hidden border border-border flex justify-center min-h-[500px]"
            >
              <div
                className="fb-page"
                data-href="https://www.facebook.com/www.bunadstuggu.no"
                data-tabs="timeline"
                data-width=""
                data-height="500"
                data-small-header="false"
                data-adapt-container-width="true"
                data-hide-cover="false"
                data-show-facepile="true"
              >
                <blockquote
                  cite="https://www.facebook.com/www.bunadstuggu.no"
                  className="fb-xfbml-parse-ignore"
                >
                  <a href="https://www.facebook.com/www.bunadstuggu.no">
                    Laster Facebook...
                  </a>
                </blockquote>
              </div>
            </div>
          </div>

          {/* Instagram Section */}
          <div className="flex flex-col">
            <a
              href="https://www.instagram.com/bunadstuggu/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Besøk Bunadstuggu på Instagram"
              className="flex items-center justify-between mb-4 group p-4 rounded-lg border border-border hover:border-primary/50 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#833AB4]/10 via-[#FD1D1D]/10 to-[#FCAF45]/10 flex items-center justify-center" aria-hidden="true">
                  <Instagram className="w-6 h-6 text-[#E1306C]" />
                </div>
                <div className="text-left">
                  <span className="font-sans text-base text-foreground font-medium block">
                    Bunadstuggu
                  </span>
                  <span className="font-sans text-xs text-muted-foreground">
                    @bunadstuggu
                  </span>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
            </a>
            <div className="w-full bg-card rounded-lg overflow-hidden border border-border min-h-[500px] flex items-center justify-center p-4">
              <blockquote 
                className="instagram-media" 
                data-instgrm-permalink="https://www.instagram.com/bunadstuggu/"
                data-instgrm-version="14"
                style={{
                  background: '#FFF',
                  border: 0,
                  borderRadius: '3px',
                  boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                  margin: '1px',
                  maxWidth: '540px',
                  minWidth: '280px',
                  padding: 0,
                  width: '99.375%'
                }}
              >
                <div style={{ padding: '16px' }}>
                  <a 
                    href="https://www.instagram.com/bunadstuggu/" 
                    style={{ 
                      background: '#FFFFFF',
                      lineHeight: 0,
                      padding: '0 0',
                      textAlign: 'center',
                      textDecoration: 'none',
                      width: '100%'
                    }} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <div style={{ backgroundColor: '#F4F4F4', borderRadius: '50%', height: '40px', marginRight: '14px', width: '40px' }}></div>
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ backgroundColor: '#F4F4F4', borderRadius: '4px', height: '14px', marginBottom: '6px', width: '100px' }}></div>
                        <div style={{ backgroundColor: '#F4F4F4', borderRadius: '4px', height: '14px', width: '60px' }}></div>
                      </div>
                    </div>
                    <div style={{ padding: '19% 0' }}></div>
                    <div style={{ display: 'block', height: '50px', margin: '0 auto 12px', width: '50px' }}>
                      <Instagram className="w-full h-full text-[#E1306C]" />
                    </div>
                    <div style={{ paddingTop: '8px' }}>
                      <div style={{ color: '#3897f0', fontFamily: 'Arial,sans-serif', fontSize: '14px', fontWeight: 550, lineHeight: '18px' }}>
                        Se profilen på Instagram
                      </div>
                    </div>
                  </a>
                </div>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialMedia;
